/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { analyticsApi } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import HelpButton from "@/components/HelpButton";
import {
    ChartLineUp, Briefcase, Clock, CurrencyInr, ChartBar, Trophy, TrendUp,
    Calendar as CalendarIcon, Scroll as ScrollIcon, WarningOctagon,
} from "@phosphor-icons/react";

function fmtINR(n) {
    if (n == null) return "—";
    return "₹ " + Number(n).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmtPct(n) {
    if (n == null) return "—";
    return `${Number(n).toFixed(1)}%`;
}

export default function Analytics() {
    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="analytics-page">
            <div className="rule-line pt-3 mb-6">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Insights</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Analytics & Reports</h1>
                    <HelpButton topic="analytics" testid="help-analytics" />
                </div>
                <p className="text-sm text-muted-foreground mt-1">Practice KPIs, outcomes, financial trends and productivity over time.</p>
            </div>

            <Tabs defaultValue="practice">
                <TabsList>
                    <TabsTrigger value="practice" data-testid="analytics-tab-practice"><Briefcase size={13} className="mr-1" /> Practice</TabsTrigger>
                    <TabsTrigger value="outcomes" data-testid="analytics-tab-outcomes"><Trophy size={13} className="mr-1" /> Outcomes</TabsTrigger>
                    <TabsTrigger value="financial" data-testid="analytics-tab-financial"><CurrencyInr size={13} className="mr-1" /> Financial</TabsTrigger>
                    <TabsTrigger value="productivity" data-testid="analytics-tab-productivity"><Clock size={13} className="mr-1" /> Productivity</TabsTrigger>
                </TabsList>
                <TabsContent value="practice" className="pt-4"><PracticeTab /></TabsContent>
                <TabsContent value="outcomes" className="pt-4"><OutcomesTab /></TabsContent>
                <TabsContent value="financial" className="pt-4"><FinancialTab /></TabsContent>
                <TabsContent value="productivity" className="pt-4"><ProductivityTab /></TabsContent>
            </Tabs>
        </div>
    );
}

// ============== Practice ==============

function PracticeTab() {
    const [data, setData] = useState(null);
    useEffect(() => { analyticsApi.practice().then(setData).catch(() => {}); }, []);
    if (!data) return <Loading />;
    return (
        <div className="space-y-6" data-testid="practice-tab">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi icon={Briefcase} label="Open matters" value={data.open_matters} sub={`${data.total_matters} total`} />
                <Kpi icon={Trophy} label="Win rate" value={fmtPct(data.win_rate_pct)} tone={data.win_rate_pct >= 50 ? "emerald" : "amber"} sub={`${data.won}W · ${data.lost}L · ${data.settled}S`} />
                <Kpi icon={Clock} label="Avg disposal" value={data.avg_disposal_days != null ? `${Math.round(data.avg_disposal_days)} d` : "—"} sub="from open → close" />
                <Kpi icon={CalendarIcon} label="Upcoming hearings" value={data.upcoming_hearings_30d} sub="next 30 days" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi icon={ScrollIcon} label="Drafts shipped (FY)" value={data.drafts_shipped_fy} sub={`since ${data.fy_start}`} />
                <Kpi icon={CalendarIcon} label="Hearings held (FY)" value={data.held_hearings_fy} sub="" />
                <Kpi icon={WarningOctagon} label="Tasks overdue" value={data.tasks_overdue} tone={data.tasks_overdue > 0 ? "destructive" : "default"} />
                <Kpi icon={CalendarIcon} label="Tasks due (7d)" value={data.tasks_due_week} tone={data.tasks_due_week > 0 ? "amber" : "default"} />
            </div>
        </div>
    );
}

// ============== Outcomes ==============

function OutcomesTab() {
    const [groupBy, setGroupBy] = useState("court");
    const [data, setData] = useState(null);
    useEffect(() => { analyticsApi.outcomes(groupBy).then(setData).catch(() => {}); }, [groupBy]);
    return (
        <div className="space-y-4" data-testid="outcomes-tab">
            <div className="flex justify-between flex-wrap gap-2 items-center">
                <div className="text-sm text-muted-foreground">Outcome breakdown grouped by</div>
                <Select value={groupBy} onValueChange={setGroupBy}>
                    <SelectTrigger className="w-[200px]" data-testid="outcomes-groupby"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="court">Court</SelectItem>
                        <SelectItem value="practice_area">Practice area</SelectItem>
                        <SelectItem value="judge">Judge</SelectItem>
                        <SelectItem value="stage">Stage</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            {!data ? <Loading /> : !data.rows.length ? (
                <Empty text="No data yet — close some matters as won/lost/settled to populate this view." />
            ) : (
                <div className="border border-border bg-white rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted text-muted-foreground">
                            <tr>
                                <th className="text-left p-2">{groupBy.replace(/_/g, " ")}</th>
                                <th className="text-right p-2">Total</th>
                                <th className="text-right p-2">Open</th>
                                <th className="text-right p-2">Won</th>
                                <th className="text-right p-2">Lost</th>
                                <th className="text-right p-2">Settled</th>
                                <th className="text-right p-2">Win rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((r) => (
                                <tr key={r.key} className="border-t border-border" data-testid={`outcome-row-${r.key}`}>
                                    <td className="p-2 font-medium">{r.key}</td>
                                    <td className="p-2 text-right">{r.total}</td>
                                    <td className="p-2 text-right text-muted-foreground">{r.open}</td>
                                    <td className="p-2 text-right text-emerald-700">{r.won}</td>
                                    <td className="p-2 text-right text-destructive">{r.lost}</td>
                                    <td className="p-2 text-right">{r.settled}</td>
                                    <td className="p-2 text-right font-mono">{fmtPct(r.win_rate_pct)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ============== Financial ==============

function FinancialTab() {
    const [period, setPeriod] = useState("fy");
    const [data, setData] = useState(null);
    useEffect(() => { analyticsApi.financial(period).then(setData).catch(() => {}); }, [period]);

    // Find max for chart scaling
    const maxBar = useMemo(() => {
        if (!data) return 1;
        return Math.max(1, ...data.monthly.map((m) => Math.max(m.invoiced, m.collected, m.expenses)));
    }, [data]);

    if (!data) return <Loading />;
    return (
        <div className="space-y-5" data-testid="financial-tab">
            <div className="flex justify-between gap-3 flex-wrap items-center">
                <div className="text-sm text-muted-foreground">Period: {data.start} → {data.end}</div>
                <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-[200px]" data-testid="financial-period"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="fy">Indian fiscal year</SelectItem>
                        <SelectItem value="calendar_year">Calendar year</SelectItem>
                        <SelectItem value="last_12m">Last 12 months</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Kpi icon={CurrencyInr} label="Invoiced" value={fmtINR(data.totals.invoiced)} />
                <Kpi icon={TrendUp} label="Collected" value={fmtINR(data.totals.collected)} tone="emerald" />
                <Kpi icon={CurrencyInr} label="Expenses" value={fmtINR(data.totals.expenses)} />
                <Kpi icon={ChartLineUp} label="Net" value={fmtINR(data.totals.net)} tone={data.totals.net >= 0 ? "emerald" : "destructive"} />
                <Kpi icon={WarningOctagon} label="Outstanding" value={fmtINR(data.totals.outstanding)} tone={data.totals.outstanding > 0 ? "amber" : "default"} />
            </div>

            {/* Stacked bar chart per month */}
            <div className="border border-border bg-white rounded-md p-4" data-testid="financial-chart">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Monthly trend</div>
                <div className="overflow-x-auto">
                    <div className="flex items-end gap-1 min-w-[600px] h-[200px]">
                        {data.monthly.map((m) => {
                            const h = (v) => `${(v / maxBar) * 100}%`;
                            return (
                                <div key={m.month} className="flex-1 flex flex-col items-center gap-1" title={`${m.month}: invoiced ${fmtINR(m.invoiced)}, collected ${fmtINR(m.collected)}`}>
                                    <div className="w-full flex gap-0.5 items-end h-full">
                                        <div className="flex-1 bg-primary/40 rounded-t" style={{ height: h(m.invoiced) }} />
                                        <div className="flex-1 bg-emerald-500/60 rounded-t" style={{ height: h(m.collected) }} />
                                        <div className="flex-1 bg-amber-400/60 rounded-t" style={{ height: h(m.expenses) }} />
                                    </div>
                                    <div className="text-[10px] text-muted-foreground">{m.month.slice(5)}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-primary/40 inline-block" /> invoiced</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500/60 inline-block" /> collected</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-amber-400/60 inline-block" /> expenses</span>
                </div>
            </div>

            {/* By-client */}
            {data.by_client?.length > 0 && (
                <div className="border border-border bg-white rounded-md overflow-hidden" data-testid="financial-by-client">
                    <div className="bg-muted text-[10px] uppercase tracking-[0.3em] text-muted-foreground p-2 px-3">Top clients in this period</div>
                    <table className="w-full text-sm">
                        <thead className="text-muted-foreground border-b border-border">
                            <tr>
                                <th className="text-left p-2">Client</th>
                                <th className="text-right p-2">Invoiced</th>
                                <th className="text-right p-2">Collected</th>
                                <th className="text-right p-2">Outstanding</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.by_client.map((c) => (
                                <tr key={c.client_id} className="border-t border-border">
                                    <td className="p-2">{c.client_name}</td>
                                    <td className="p-2 text-right">{fmtINR(c.invoiced)}</td>
                                    <td className="p-2 text-right text-emerald-700">{fmtINR(c.collected)}</td>
                                    <td className="p-2 text-right text-amber-700">{fmtINR(c.outstanding)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ============== Productivity ==============

function ProductivityTab() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    useEffect(() => { analyticsApi.productivity(days).then(setData).catch(() => {}); }, [days]);

    const maxHrs = useMemo(() => {
        if (!data) return 1;
        return Math.max(1, ...data.series.map((s) => s.billable_hours + s.non_billable_hours));
    }, [data]);

    if (!data) return <Loading />;
    return (
        <div className="space-y-5" data-testid="productivity-tab">
            <div className="flex justify-between gap-3 flex-wrap items-center">
                <div className="text-sm text-muted-foreground">{data.start} → {data.end}</div>
                <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
                    <SelectTrigger className="w-[160px]" data-testid="productivity-days"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="7">Last 7 days</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                        <SelectItem value="90">Last 90 days</SelectItem>
                        <SelectItem value="180">Last 180 days</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi icon={Clock} label="Total hours" value={`${data.total_hours} h`} />
                <Kpi icon={ChartBar} label="Billable hours" value={`${data.billable_hours} h`} tone="emerald" />
                <Kpi icon={TrendUp} label="Billable %" value={fmtPct(data.billable_pct)} tone={data.billable_pct >= 60 ? "emerald" : "amber"} />
                <Kpi icon={CurrencyInr} label="Billable amount" value={fmtINR(data.billable_amount)} />
            </div>

            <div className="border border-border bg-white rounded-md p-4" data-testid="productivity-chart">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Daily hours (stacked: billable vs non-billable)</div>
                <div className="overflow-x-auto">
                    <div className="flex items-end gap-0.5 min-w-[600px] h-[160px]">
                        {data.series.map((s) => {
                            const total = s.billable_hours + s.non_billable_hours;
                            const h = (v) => `${(v / maxHrs) * 100}%`;
                            return (
                                <div key={s.date} className="flex-1 flex flex-col items-center" title={`${s.date}: ${s.billable_hours}h billable / ${s.non_billable_hours}h non-billable`}>
                                    <div className="w-full flex flex-col-reverse h-full justify-end">
                                        <div className="bg-emerald-500/60 w-full" style={{ height: h(s.billable_hours) }} />
                                        <div className="bg-muted-foreground/30 w-full" style={{ height: h(s.non_billable_hours) }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500/60 inline-block" /> billable</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-muted-foreground/30 inline-block" /> non-billable</span>
                </div>
            </div>

            {data.top_matters?.length > 0 && (
                <div className="border border-border bg-white rounded-md overflow-hidden" data-testid="productivity-top-matters">
                    <div className="bg-muted text-[10px] uppercase tracking-[0.3em] text-muted-foreground p-2 px-3">Top matters by time invested</div>
                    <table className="w-full text-sm">
                        <thead className="text-muted-foreground border-b border-border">
                            <tr>
                                <th className="text-left p-2">Matter</th>
                                <th className="text-right p-2">Hours</th>
                                <th className="text-right p-2">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.top_matters.map((m) => (
                                <tr key={m.matter_id} className="border-t border-border">
                                    <td className="p-2">{m.title}</td>
                                    <td className="p-2 text-right font-mono">{m.hours} h</td>
                                    <td className="p-2 text-right">{fmtINR(m.amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ============== Shared ==============

function Kpi({ icon: Icon, label, value, sub, tone = "default" }) {
    const ring = {
        emerald: "border-emerald-300 bg-emerald-50/40",
        amber: "border-amber-300 bg-amber-50/40",
        destructive: "border-destructive/40 bg-destructive/5",
        default: "border-border bg-white",
    }[tone];
    return (
        <div className={`border rounded-md p-3 ${ring}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1">
                <Icon size={11} /> {label}
            </div>
            <div className="font-serif text-2xl mt-1">{value}</div>
            {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
        </div>
    );
}

function Loading() {
    return <div className="text-sm text-muted-foreground p-6">Loading…</div>;
}
function Empty({ text }) {
    return <div className="border border-dashed border-border rounded-md p-6 text-sm text-muted-foreground text-center">{text}</div>;
}
